import { useState } from 'react';
import { Bell, Search, Menu, Phone, X } from 'lucide-react';
import { clsx } from 'clsx';
import { useCallStore } from '@/stores/callStore';
import { useRBAC } from '@/hooks/useRBAC';
import { getInitials } from '@/utils/formatters';
import { ROLE_LABELS } from '@/utils/permissions';
import type { Role } from '@/types';

interface HeaderProps {
  onMenuToggle: () => void;
  title?: string;
}

export function Header({ onMenuToggle, title }: HeaderProps) {
  const { user, role } = useRBAC();
  const incomingCalls = useCallStore(s => s.incomingCalls);
  const dismissCall = useCallStore(s => s.dismissIncomingCall);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <>
      <header className="h-14 bg-white border-b border-slate-100 flex items-center gap-4 px-4 sticky top-0 z-30 shadow-sm">
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 lg:hidden"
        >
          <Menu size={20} />
        </button>

        {title && <h1 className="text-base font-semibold text-slate-900 hidden lg:block">{title}</h1>}

        {/* Search */}
        <div className="flex-1 max-w-sm hidden md:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search leads, patients..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-9 pr-3 rounded-lg border border-slate-200 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan transition-all"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Notifications bell */}
          <button className="relative p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <Bell size={20} />
            {incomingCalls.length > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full animate-pulse-dot" />
            )}
          </button>

          {/* Live call indicator */}
          {incomingCalls.length > 0 && (
            <button className="flex items-center gap-1.5 h-8 px-3 bg-green-50 text-green-700 rounded-lg text-xs font-medium border border-green-200 animate-pulse">
              <Phone size={12} className="fill-green-600" />
              {incomingCalls.length} Incoming
            </button>
          )}

          {/* User avatar */}
          <div className="flex items-center gap-2 pl-2 border-l border-slate-200">
            <div className="w-8 h-8 rounded-full bg-brand-navy/10 text-brand-navy flex items-center justify-center text-xs font-bold">
              {getInitials(user?.name)}
            </div>
            <div className="hidden sm:block">
              <div className="text-xs font-semibold text-slate-800">{user?.name}</div>
              <div className="text-xs text-slate-400">{role ? ROLE_LABELS[role as Role] : ''}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Incoming Call Notifications */}
      {incomingCalls.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 space-y-2">
          {incomingCalls.slice(0, 3).map(call => (
            <div key={call.callId} className="bg-white rounded-xl shadow-2xl border border-green-200 p-4 w-72 animate-slide-in">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                  <Phone size={18} className="text-white fill-white" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 text-sm">
                    {call.isRepeatCaller ? '🔁 Repeat Caller' : '📞 New Incoming Call'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {call.isRepeatCaller ? 'This is an existing patient' : 'New caller waiting'}
                  </div>
                </div>
                <button onClick={() => dismissCall(call.callId)} className="text-slate-400 hover:text-slate-600">
                  <X size={16} />
                </button>
              </div>
              <div className="mt-3 flex gap-2">
                <button className="flex-1 h-8 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600">
                  Accept
                </button>
                <button
                  onClick={() => dismissCall(call.callId)}
                  className="flex-1 h-8 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
